import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import { DEFAULT_THEME_MODE, OpsTokens, TOKENS, ThemeMode } from "./tokens";
import { themes } from "./buildTheme";

interface ThemeModeValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  tokens: OpsTokens;
}

const ThemeModeContext = createContext<ThemeModeValue | undefined>(undefined);

export const useThemeMode = (): ThemeModeValue => {
  const value = useContext(ThemeModeContext);
  if (!value) throw new Error("useThemeMode must be used inside <OpsThemeProvider>");
  return value;
};

export const OpsThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [stored, setStored] = useLocalStorage<ThemeMode>(STORAGE_KEYS.THEME_MODE, DEFAULT_THEME_MODE);

  const mode: ThemeMode = stored === "dark" ? "dark" : "light";

  const setMode = useCallback(
    (next: ThemeMode) => {
      setStored(next);
    },
    [setStored],
  );

  const toggleMode = useCallback(() => setMode(mode === "dark" ? "light" : "dark"), [mode, setMode]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  useLayoutEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined;
    (window as any).__setThemeMode = setMode;
    return () => {
      delete (window as any).__setThemeMode;
    };
  }, [setMode]);

  const value = useMemo<ThemeModeValue>(() => ({ mode, setMode, toggleMode, tokens: TOKENS[mode] }), [mode, setMode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={themes[mode]}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
